import type { PlatformActionDetail } from '../models/platformAction.types'
import { adaptDetail } from '../service/adaptActionRequest'
import { classifyError, isCanceled } from '../service/classifyError'

import type { DetailActionApi, DetailActionStore, FlowResult } from './platformActionFlow.types'

export interface LoadActionDetailFlowDeps {
  api: DetailActionApi
  store: DetailActionStore
}

export interface LoadActionDetailOptions {
  /**
   * Skip the `loading` transition. Poll ticks use this: the drawer already shows the request, and
   * flashing a skeleton every three seconds would be worse than a stale-by-3s status line.
   */
  silent?: boolean
}

/** Loads one request with its approval + audit chains. Also the polling tick (`silent: true`). */
export class LoadActionDetailFlow {
  constructor(private readonly deps: LoadActionDetailFlowDeps) {}

  async execute(
    workspaceId: number,
    actionId: string,
    options: LoadActionDetailOptions = {},
    signal?: AbortSignal,
  ): Promise<FlowResult<PlatformActionDetail> | null> {
    if (!options.silent) this.deps.store.setDetailLoading()

    try {
      const dto = await this.deps.api.detail(workspaceId, actionId, signal)
      if (signal?.aborted === true) return null
      const detail = adaptDetail(dto)
      this.deps.store.setDetail(detail)
      return { ok: true, value: detail }
    } catch (cause) {
      const error = classifyError(cause)
      if (isCanceled(error)) return null
      // A silent tick must not blank a drawer that is already rendering a request; surface the
      // error to the caller (polling stops) and leave the last good detail on screen.
      if (!options.silent) this.deps.store.setDetailError(error)
      return { ok: false, error }
    }
  }
}
